import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { lastValueFrom, Observable, of, Subject } from 'rxjs';
import { Person } from 'src/app/people/models/person';

import { EmailType, IEmailMessageWrite } from 'src/app/shared/models/emailMessage';
import { BulkEmailMessageWrite } from '../models/bulkEmailMessageWrite';
import { EmailCustomization } from '../models/emailCustomization';
import { ApiVersions } from './api-version';
import { BaseService } from './base.service';
import { EmailPreferencesService } from './email-preferences.service';

const MaxEmailRecipientCount = 10_000;

@Injectable()
export class EmailPreparationService extends BaseService {
  public readonly emailSent = new Subject<void>();

  private baseUsersPath: string = 'users';
  private onPreparedCallback: (result: EmailPreparedResult) => void;

  constructor(private emailPreferencesService: EmailPreferencesService) {
    super();
  }

  openSendEmailDrawer(
    relativeTo: ActivatedRoute,
    preparation: EmailPreparation,
    onPrepared: (result: EmailPreparedResult) => void
  ) {
    var stateData = {
      state: {
        data: preparation
      },
      relativeTo: relativeTo
    };

    this.onPreparedCallback = onPrepared;
    if (preparation.navigateTo) {
      this.router.navigate([preparation.navigateTo], stateData);
    } else {
      this.router.navigate([EmailNavigationPath.sendEmail], stateData);
    }
  }

  onEmailPrepared(result: EmailPreparedResult) {
    this.onPreparedCallback(result);
    this.onPreparedCallback = null;
    this.emailSent.next();
  }

  sendIndividualEmail(userId: string, message: IEmailMessageWrite, action: EmailServerActions) {
    const payload = JSON.stringify(message);
    const urlPath = `${this.baseUsersPath}/${userId}/${action}`;
    return this.post(urlPath, payload).subscribe(() => {
      this.openSnackBar(`Successfully sent email.`, true);
    });
  }

  sendBulkEmail(message: BulkEmailMessageWrite) {
    return this.post(
      `${this.baseUsersPath}/bulk-email`,
      JSON.stringify(message),
      null,
      ApiVersions.v0_1,
      true // rethrow errors to prevent the success callback if there's an error
    ).subscribe(() => {
      this.openSnackBar('Successfully sent email.', true);
    });
  }

  sendBenchTeamEmail(teamId: string, message: BenchTeamEmailMessageWrite) {
    return this.post(
      `teams/${teamId}/bulkBenchEmail`,
      JSON.stringify(message),
      null,
      ApiVersions.v0_1,
      true
    ).subscribe(() => {
      this.openSnackBar('Successfully sent email.', true);
    });
  }

  async getCredentialCoverageWarning(peopleIds: string[], content: string): Promise<string | null> {
    const credentialDefinitionIds = this.extractReferencedCredentialDefinitionIds(content);
    if (!peopleIds?.length || !credentialDefinitionIds.length) {
      return null;
    }

    try {
      const results: CredentialCoverageResult[] = await lastValueFrom(
        this.post(
          `${this.baseUsersPath}/credential-coverage`,
          JSON.stringify({ personIds: peopleIds, credentialDefinitionIds }),
          null,
          ApiVersions.v0_1
        )
      );
      return this.prepareCredentialCoverageMessage(results);
    } catch {
      return null;
    }
  }

  private extractReferencedCredentialDefinitionIds(content: string): string[] {
    if (!content) {
      return [];
    }
    const pattern = /personCredential(?:Application)?\.\[([0-9a-fA-F-]{36})\]/g;
    const ids = new Set<string>();
    let match: RegExpExecArray;
    while ((match = pattern.exec(content))) {
      ids.add(match[1]);
    }
    return Array.from(ids);
  }

  private prepareCredentialCoverageMessage(results: CredentialCoverageResult[]): string | null {
    const missing = results?.filter((r) => r.missingCount > 0) ?? [];
    if (!missing.length) {
      return null;
    }
    return missing
      .map(
        (r) =>
          `${r.missingCount} of ${r.totalCount} recipients don't have the "${r.credentialDefinitionName}" credential - those merge fields will be blank for them.`
      )
      .join(' ');
  }

  prepareFooter(data: EmailPreparation, isRecipientInvalid: boolean): string {
    var footer: string = '';

    if (data?.person) {
      var person: Person = data?.person;
      footer = isRecipientInvalid
        ? person.fullName +
          ' (' +
          person.email +
          ') has opted out of receiving group and individual emails.'
        : 'Send email to ' + person.fullName + ' (' + person.email + ') ';
      return footer;
    }
    if (data?.filteredPeopleCount) {
      footer = isRecipientInvalid
        ? `Cannot send to more than ${MaxEmailRecipientCount} recipients at a time.`
        : `Send email to ${data?.filteredPeopleCount} people.`;
      return footer;
    }

    return footer;
  }

  isRecipientInvalid(data: EmailPreparation): Observable<boolean> {
    var isRecipientInvalid;
    if (data?.person) {
      var person: Person = data?.person;
      var subject = new Subject<boolean>();
      this.emailPreferencesService
        .getCommercialEmailPreference(person.id)
        .subscribe((emailPref) => {
          isRecipientInvalid = !emailPref.isOptedIn;
          subject.next(isRecipientInvalid);
        });

      return subject.asObservable();
    }

    if (data?.filteredPeopleCount) {
      isRecipientInvalid = data?.filteredPeopleCount > MaxEmailRecipientCount;
      return of(isRecipientInvalid);
    }

    return of(true);
  }
}

export interface EmailPreparation {
  emailType: EmailType;
  title: string;
  templateName: string;
  person?: Person;
  peopleIds?: string[];
  filteredPeopleCount?: number;
  emailCustomization?: EmailCustomization;
  sendButtonText?: string;
  cancelButtonText?: string;
  navigateTo?: EmailNavigationPath;
}

export interface EmailPreparedResult {
  emailType: EmailType;
  emailMessage: IEmailMessageWrite;
}

export enum EmailServerActions {
  email = 'email',
  portalInvite = 'portal-invite'
}

export enum EmailNavigationPath {
  sendEmail = 'sendEmail',
  portalInvite = 'portalInvite'
}

export interface BenchTeamEmailMessageWrite {
  fromName: string;
  replyTo: string;
  subject: string;
  content: string;
  peopleIds: string[];
}

export interface CredentialCoverageResult {
  credentialDefinitionId: string;
  credentialDefinitionName: string;
  missingCount: number;
  totalCount: number;
}
